/**
 * createI18n.js — a small, reusable i18n factory. Domain-agnostic and Firebase-free
 * so it drops into any page and is trivial to unit-test.
 *
 * One call configures everything and returns a ready-to-use bundle:
 *
 *   const { LangProvider, useLang, useAutoTranslate, useEngine, LANGS } = createI18n({
 *     languages: [{ id:'en', label:'EN', name:'English' }, { id:'te', label:'తె', name:'తెలుగు' }],
 *     resources: { en: {...}, te: {...} },   // UI strings, keyed by dotted keys
 *     terms:     [['Eggs','గుడ్లు'], …],      // optional curated item dictionary
 *     termLangs: ['en','te'],                // what the pair positions mean (defaults to first 2 langs)
 *     storageKey: 'do_lang',                 // per-device language choice
 *   });
 *   // Dynamic auto-fill is opt-in: wire `useEngine(cache)` into <LangProvider engine={…}>
 *   // (omit it and the provider works with just the curated dictionary + as-typed).
 *
 * Three localization layers, in order: (1) curated `terms`, (2) a shared/auto cache
 * (via useEngine), (3) the source text as-typed. UI strings fall back source-lang → key,
 * so a missing translation degrades gracefully and never renders blank.
 *
 * NEW LANGUAGE: add it to `languages` and (optionally) a `resources[id]` bundle — item
 * names auto-translate, UI strings fall back to the source language until translated.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { translateMany } from './providers';

const DEFAULT_NORM = s => String(s || '').toLowerCase().trim().replace(/[.,;:!?]+$/, '').replace(/\s+/g, ' ');
const AUTO_DEBOUNCE_MS = 800;
/** Longest a batch of auto-translations waits for the shared cache's first load. */
const LOAD_WAIT_MS = 4000;

export function createI18n(config) {
  const {
    languages,
    resources = {},
    terms = [],
    termLangs,
    storageKey = 'app_lang',
    defaultLang = (languages[0] && languages[0].id) || 'en',
    sourceLang = defaultLang,           // language item names are typed in (translate FROM this)
    norm = DEFAULT_NORM,
  } = config;

  const langIds = languages.map(l => l.id);
  const pairLangs = termLangs || langIds.slice(0, 2);

  // Bidirectional term index: norm(label in ANY language) → the whole entry {en, te, …}.
  const TERM_INDEX = new Map();
  terms.forEach((entry) => {
    const obj = Array.isArray(entry)
      ? pairLangs.reduce((o, lid, i) => { o[lid] = entry[i]; return o; }, {})
      : entry;
    Object.values(obj).forEach((label) => { if (label) TERM_INDEX.set(norm(label), obj); });
  });

  const isKnownTerm = (name) => TERM_INDEX.has(norm(name));
  const hasTerm = (name, lang) => { const e = TERM_INDEX.get(norm(name)); return !!(e && e[lang]); };

  /** Localize an item name: curated term → shared/auto cache → as-typed source. */
  function localize(name, lang, cache) {
    const key = norm(name);
    const entry = TERM_INDEX.get(key);
    if (entry && entry[lang]) return entry[lang];
    if (lang !== sourceLang && cache) {
      const hit = cache.get(key);
      const t = hit && ((hit.t && hit.t[lang]) || hit[lang]);   // new schema + legacy {en,te}
      if (t) return t;
    }
    return name;
  }

  /** Look up a UI string: resources[lang] → resources[source] → raw key; interpolates {vars}. */
  function translate(lang, key, vars) {
    let s = (resources[lang] && resources[lang][key]) ?? (resources[defaultLang] && resources[defaultLang][key]) ?? key;
    if (vars) s = String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
    return s;
  }

  const EMPTY_ENGINE = { cache: new Map(), request: () => {}, correct: () => {} };

  const Ctx = createContext({
    lang: defaultLang, setLang: () => {}, languages,
    t: (k) => k, ln: (n) => n, requestTranslations: () => {}, setTranslation: () => {},
  });

  function LangProvider({ children, engine = EMPTY_ENGINE }) {
    const [lang, setLangState] = useState(() => {
      try { const v = localStorage.getItem(storageKey); return langIds.includes(v) ? v : defaultLang; }
      catch { return defaultLang; }
    });
    const setLang = useCallback((l) => {
      setLangState(l);
      try { localStorage.setItem(storageKey, l); } catch { /* storage off */ }
    }, []);
    const { cache, request, correct, activate } = engine;
    // The shared translation cache is only ever consulted for a non-source language
    // (localize() skips it for sourceLang), so an English screen never needs it —
    // don't pay a whole-collection Firestore read for nothing. Load it the first time
    // this device shows another language.
    useEffect(() => { if (lang !== sourceLang && activate) activate(); }, [lang, activate]);
    const value = useMemo(() => ({
      lang, setLang, languages,
      t: (key, vars) => translate(lang, key, vars),
      ln: (name) => localize(name, lang, cache),
      // Consumer-facing helpers auto-bind the current language, so callers stay simple.
      requestTranslations: (names) => request(names, lang),
      setTranslation: (source, text) => correct(source, lang, text),
    }), [lang, setLang, cache, request, correct]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  const useLang = () => useContext(Ctx);

  /** Feed a screen's item names in — auto-translates the ones not already covered. */
  function useAutoTranslate(names) {
    const { lang, requestTranslations } = useLang();
    const key = Array.isArray(names) ? names.join('|') : '';
    useEffect(() => {
      if (lang !== sourceLang && names && names.length) requestTranslations(names);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, key, requestTranslations]);
  }

  /**
   * The dynamic auto-fill engine, injected into LangProvider. `cache` (optional) is a
   * shared Firestore cache (createFirestoreCache) for cross-device sharing; omit it to
   * get in-memory-only translation. Returns { cache, request, correct, activate } —
   * the shared cache is only read after LangProvider calls `activate`.
   */
  function useEngine(cache) {
    const [store, setStore] = useState(() => new Map());
    const storeRef = useRef(store);
    const attempted = useRef(new Set());   // `${lang}:${normName}` we've already tried this session
    const pending = useRef([]);            // [{ name, lang }] debounce buffer
    const timer = useRef(null);
    // Set by LangProvider once a non-source language is on screen; until then the
    // shared cache is not read at all (see the `activate` effect in LangProvider).
    const [active, setActive] = useState(false);
    const activate = useCallback(() => setActive(true), []);
    // Resolves once the shared cache's one-shot load has answered. The cache now
    // starts loading only when a non-source language appears — the same moment the
    // screen asks for translations — so a batch must wait for it, or it would
    // machine-translate (and re-check in Firestore) names the cache already covers.
    const loadGate = useRef(null);
    const remoteRef = useRef(null);         // last loaded shared map, before it reaches `store`

    useEffect(() => { storeRef.current = store; }, [store]);

    // Always clear a pending debounce on unmount (covers the cache-less in-memory mode too).
    useEffect(() => () => clearTimeout(timer.current), []);

    useEffect(() => {
      if (!active) return undefined;
      // `load` is the one-shot reader (i18n/firestoreCache.js — a listener here was
      // re-reading the whole collection on every mount). `subscribe` is the older
      // realtime shape, still honoured so custom caches keep working.
      const read = cache && (cache.load || cache.subscribe);
      if (typeof read !== 'function') return undefined;
      // Only the one-shot `load` is waited on; a legacy `subscribe` may never call back.
      let open = null;
      if (typeof cache.load === 'function') {
        loadGate.current = new Promise((resolve) => { open = resolve; });
      }
      // Merge remote (shared/human) over local, but keep local auto entries not yet in
      // Firestore — so translations still show if the write is denied/offline.
      const stop = read.call(cache, (remote) => {
        remoteRef.current = remote;
        if (open) open();
        setStore((prev) => {
          const next = new Map(remote);
          prev.forEach((v, k) => { if (!next.has(k)) next.set(k, v); });
          return next;
        });
      }, () => { if (open) open(); });
      return () => {
        if (open) open();                 // never leave a batch waiting on a torn-down load
        if (typeof stop === 'function') stop();
      };
    }, [cache, active]);

    const mergeIn = useCallback((source, lang, text, srcType) => {
      setStore((prev) => {
        const next = new Map(prev);
        const k = norm(source);
        const cur = next.get(k) || { en: source, t: {}, src: {} };
        if (cur.src && cur.src[lang] === 'human' && srcType !== 'human') return prev;  // don't downgrade
        next.set(k, { ...cur, en: source, t: { ...cur.t, [lang]: text }, src: { ...cur.src, [lang]: srcType } });
        return next;
      });
    }, []);

    const flush = useCallback(async () => {
      let batch = pending.current;
      pending.current = [];
      if (!batch.length) return;
      if (loadGate.current) {
        let t;
        await Promise.race([loadGate.current, new Promise((r) => { t = setTimeout(r, LOAD_WAIT_MS); })]);
        clearTimeout(t);
        // Drop what the shared cache turned out to cover already.
        const covered = ({ name, lang }) => {
          const k = norm(name);
          const hit = storeRef.current.get(k) || (remoteRef.current && remoteRef.current.get(k));
          return !!(hit && ((hit.t && hit.t[lang]) || hit[lang]));
        };
        batch = batch.filter((b) => !covered(b));
        if (!batch.length) return;
      }
      const byLang = new Map();
      batch.forEach(({ name, lang }) => { if (!byLang.has(lang)) byLang.set(lang, []); byLang.get(lang).push(name); });
      for (const [lang, names] of byLang) {
        const map = await translateMany(names, sourceLang, lang);      // eslint-disable-line no-await-in-loop
        map.forEach((text, src) => {
          mergeIn(src, lang, text, 'auto');                            // 1) show immediately
          if (cache && typeof cache.saveAuto === 'function') cache.saveAuto(src, lang, text).catch(() => {}); // 2) share
        });
      }
    }, [cache, mergeIn]);

    const request = useCallback((names, lang) => {
      let queued = false;
      (names || []).forEach((n) => {
        const k = norm(n);
        const ak = `${lang}:${k}`;
        const cached = storeRef.current.get(k);
        const already = cached && ((cached.t && cached.t[lang]) || cached[lang]);
        if (!k || hasTerm(n, lang) || already || attempted.current.has(ak)) return;
        attempted.current.add(ak);
        pending.current.push({ name: n, lang });
        queued = true;
      });
      if (queued) { clearTimeout(timer.current); timer.current = setTimeout(flush, AUTO_DEBOUNCE_MS); }
    }, [flush]);

    const correct = useCallback((source, lang, text) => {
      const t = String(text || '').trim();
      if (!t) return;
      mergeIn(source, lang, t, 'human');
      if (cache && typeof cache.saveHuman === 'function') cache.saveHuman(source, lang, t).catch(() => {});
    }, [cache, mergeIn]);

    return { cache: store, request, correct, activate };
  }

  return {
    LANGS: languages, LangProvider, useLang, useAutoTranslate, useEngine,
    localize, isKnownTerm, hasTerm, norm,
  };
}
